import {
  azureEngineServiceUrl,
  azureMarketplaceServiceUrl,
  chatServiceUrl,
  cloudAccountServiceUrl,
  cloudfaasServiceUrl,
  crmServiceUrl,
  engineServiceUrl,
  gcpMarketplaceServiceUrl,
  marketplaceServiceUrl,
  identityServiceUrl,
  MarketplaceSearchUrl,
  labravAWSEngineServiceUrl,
  labravAWSEngineServiceV2Url,
  intelligenceServiceUrl,
  intelligenceChatServiceUrl,
  mappingConfigurationServiceUrl,
  labraExternalClientAccessJwtIssuer,
  JWTRegex,
} from './constants'
import { matchPath } from 'react-router-dom'

export const formatCurrency = (
  value: number,
  options?: Intl.NumberFormatOptions
) =>
  new Intl.NumberFormat('en-US', {
    ...options,
    style: 'currency',
    currency: options?.currency || 'USD',
  }).format(value)

export const getSecureKeyFromPath = (path: string) =>
  matchPath(path, window?.location?.pathname)?.params?.secureKey

export const getSecureKey = () => {
  if (typeof window !== 'undefined') {
    const jwt = window.LABRA_JWT
    if (jwt) {
      return jwt
    }
  }

  return (
    getSecureKeyFromPath('/external-apps/hubspot/v1/:secureKey/*') ||
    getSecureKeyFromPath('/external-apps/collab-room/v1/:secureKey/*') ||
    getSecureKeyFromPath('/external-apps/:secureKey/partners/*') ||
    getSecureKeyFromPath('/external-apps/embed-analytics/v1/:secureKey/*') ||
    getSecureKeyFromPath('/external-apps/:secureKey/flyout/*') ||
    getSecureKeyFromPath('/external-apps/:secureKey/resaleCppoForm/*')
  )
}

const decodeJwtPayloadSafe = <T>(token: string): T | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    let normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    while (normalized.length % 4 !== 0) {
      normalized += '='
    }
    return JSON.parse(
      decodeURIComponent(
        atob(normalized)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    ) as T
  } catch {
    return null
  }
}

/**
 * HubSpot card-auth tokens from identity `/oauth/token-auth0` include an
 * `auth_provider` claim. Decodes the JWT payload (no external dep) and returns
 * true when it was issued via auth0.
 */
export const isAuth0ProviderSecureKey = (token: string): boolean => {
  const decoded = decodeJwtPayloadSafe<{ auth_provider?: string }>(token)
  return decoded?.auth_provider === 'auth0'
}

/**
 * Classifies Gatekeeper Labra External Client Access JWTs for header selection.
 * This decodes unverified claims; Gatekeeper validates signature, `kid`,
 * audience, expiry, and authorization.
 */
export const isLabraExternalClientAccessToken = (token: string): boolean => {
  const decoded = decodeJwtPayloadSafe<{ iss?: string }>(token)
  return decoded?.iss === labraExternalClientAccessJwtIssuer
}

export type SecureKeyType =
  | 'auth0'
  | 'labra-external'
  | 'legacy-internal'
  | 'invalid'

/** Classifies an embedded JWT for shared Axios and SSE header routing. */
export const classifySecureKey = (token: string): SecureKeyType => {
  if (!new RegExp(JWTRegex).test(token)) return 'invalid'
  if (isAuth0ProviderSecureKey(token)) return 'auth0'
  if (isLabraExternalClientAccessToken(token)) return 'labra-external'
  return 'legacy-internal'
}

export const getServiceNameFromUrl = (url: string): string | undefined => {
  if (url.includes(marketplaceServiceUrl)) {
    return 'Marketplace'
  } else if (
    url.includes(engineServiceUrl) ||
    url.includes(azureEngineServiceUrl) ||
    // Absolute api.labra.io/azure_engine/v2 (and same-origin /api/azure_engine/) —
    // v2 is not a substring of the v1 constant, so Canvas Internal-Authorization
    // would otherwise omit the Bearer-azure_engine header.
    url.includes('/azure_engine/') ||
    url.includes(crmServiceUrl) ||
    url.includes(cloudAccountServiceUrl) || // TODO: Need more discussion with team about the service name for cloud account if separate service name is available
    url.includes(cloudfaasServiceUrl) // TODO: Need more discussion with team about the service name for cloud faas if separate service name is available
  ) {
    return 'Crm'
  } else if (
    url.includes(labravAWSEngineServiceUrl) ||
    url.includes(labravAWSEngineServiceV2Url) ||
    // Absolute api.labra.io/aws_engine/v2 (and same-origin /api/aws_engine/) —
    // v2 is not a substring of the v1 constant, so Canvas Internal-Authorization
    // would otherwise omit the Bearer-aws_engine header.
    url.includes('/aws_engine/')
  ) {
    return 'aws_engine'
  } else if (url.includes(azureMarketplaceServiceUrl)) {
    return 'azure_marketplace'
  } else if (url.includes(gcpMarketplaceServiceUrl)) {
    return 'gcp_marketplace'
  } else if (url.includes(identityServiceUrl)) {
    return 'identity'
  } else if (
    url.includes(intelligenceServiceUrl) ||
    url.includes(intelligenceChatServiceUrl) ||
    url.includes(chatServiceUrl) ||
    url.includes('/intelligence_chat/') ||
    url.includes('/agent_chat')
  ) {
    return 'intelligence'
  } else if (url.includes(MarketplaceSearchUrl)) {
    return 'search'
  } else if (url.includes(mappingConfigurationServiceUrl)) {
    return 'mapping_configuration'
  }
}
